import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  X,
  Settings,
  Crown,
  Shield,
  User,
  Save,
  AlertCircle,
  CheckCircle
} from 'lucide-react';
import type { EmpresaListItem, UsuarioEmpresa, UpdateUsuarioEmpresaRequest } from '../../../types';
import { useActualizarUsuarioEmpresa, getPermisosDefecto } from '../../../hooks/useUsuariosEmpresa';

interface EditUsuarioEmpresaModalProps {
  usuario: UsuarioEmpresa;
  empresa: EmpresaListItem;
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
}

const EditUsuarioEmpresaModal: React.FC<EditUsuarioEmpresaModalProps> = ({
  usuario,
  empresa,
  isOpen,
  onClose,
  onSaved
}) => {
  const [formData, setFormData] = useState<UpdateUsuarioEmpresaRequest>({
    rol_en_empresa: usuario.rol_en_empresa,
    es_empresa_principal: usuario.es_empresa_principal,
    es_activa: usuario.es_activa,
    permisos: { ...usuario.permisos }
  });

  const { t } = useTranslation();
  const [hasChanges, setHasChanges] = useState(false);

  const actualizarUsuario = useActualizarUsuarioEmpresa();

  useEffect(() => {
    // Detectar cambios
    const originalData = {
      rol_en_empresa: usuario.rol_en_empresa,
      es_empresa_principal: usuario.es_empresa_principal,
      es_activa: usuario.es_activa,
      permisos: usuario.permisos
    };

    const hasChanged = JSON.stringify(formData) !== JSON.stringify(originalData);
    setHasChanges(hasChanged);
  }, [formData, usuario]);

  if (!isOpen) return null;

  const handleRolChange = (newRol: 'administrador' | 'supervisor' | 'operador') => {
    const permisosDefecto = getPermisosDefecto(newRol);
    
    setFormData(prev => ({
      ...prev,
      rol_en_empresa: newRol,
      permisos: permisosDefecto
    }));
  };

  const handlePermisoChange = (permiso: string, valor: boolean) => {
    setFormData(prev => ({
      ...prev,
      permisos: {
        ...prev.permisos!,
        [permiso]: valor
      }
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!hasChanges) {
      onClose();
      return;
    }

    try {
      await actualizarUsuario.mutateAsync({
        empresaId: empresa.id,
        usuarioId: usuario.usuario_id,
        datos: formData
      });

      onSaved();
    } catch (error) {
      console.error('Error al actualizar usuario:', error);
    }
  };

  const handleReset = () => {
    setFormData({
      rol_en_empresa: usuario.rol_en_empresa,
      es_empresa_principal: usuario.es_empresa_principal,
      es_activa: usuario.es_activa,
      permisos: { ...usuario.permisos }
    });
  };

  const getRolIcon = (rol: string) => {
    switch (rol) {
      case 'administrador':
        return <Crown className="h-5 w-5 text-yellow-500" />;
      case 'supervisor':
        return <Shield className="h-5 w-5 text-blue-500" />;
      default:
        return <User className="h-5 w-5 text-gray-500" />;
    }
  };

  const getRolBadge = (rol: string) => {
    const configs = {
      administrador: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      supervisor: 'bg-blue-100 text-blue-800 border-blue-200',
      operador: 'bg-gray-100 text-gray-800 border-gray-200'
    };
    
    return configs[rol as keyof typeof configs] || configs.operador;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70]">
      <div 
        className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <Settings className="h-6 w-6 text-green-600" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                {t('admin.edit_item')}
              </h2>
              <p className="text-sm text-gray-600">
                {usuario.usuario_info.nombre_completo || usuario.usuario_info.username} en {empresa.nombre}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Usuario Info */}
        <div className="p-6 bg-gray-50 border-b border-gray-200">
          <div className="flex items-center space-x-4">
            <div className="h-12 w-12 bg-green-100 rounded-full flex items-center justify-center">
              <User className="h-6 w-6 text-green-600" />
            </div>
            <div className="flex-1">
              <div className="flex items-center space-x-2">
                <h3 className="font-medium text-gray-900">
                  {usuario.usuario_info.nombre_completo || usuario.usuario_info.username}
                </h3>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${
                  getRolBadge(usuario.rol_en_empresa)
                }`}>
                  {getRolIcon(usuario.rol_en_empresa)}
                  <span className="ml-1">{usuario.rol_en_empresa}</span>
                </span>
              </div>
              <p className="text-sm text-gray-600">
                @{usuario.usuario_info.username} • {usuario.usuario_info.email}
              </p>
            </div>
            <div className="flex items-center space-x-2">
              {formData.es_activa ? (
                <CheckCircle className="h-5 w-5 text-green-500" />
              ) : (
                <AlertCircle className="h-5 w-5 text-red-500" />
              )}
              <span className={`text-sm ${
                formData.es_activa ? 'text-green-700' : 'text-red-700'
              }`}>
                {formData.es_activa ? t('admin.status_active') : t('admin.status_inactive')}
              </span>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex flex-col h-full">
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {/* Estado */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                {t('common.status')}
              </label>
              <div className="flex items-center space-x-4">
                <label className="flex items-center space-x-2">
                  <input
                    type="radio"
                    name="estado"
                    checked={formData.es_activa === true}
                    onChange={() => setFormData(prev => ({ ...prev, es_activa: true }))}
                    className="text-green-600 focus:ring-green-500"
                  />
                  <span className="text-sm text-gray-700">{t('admin.status_active')}</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input
                    type="radio"
                    name="estado"
                    checked={formData.es_activa === false}
                    onChange={() => setFormData(prev => ({ ...prev, es_activa: false }))}
                    className="text-red-600 focus:ring-red-500"
                  />
                  <span className="text-sm text-gray-700">{t('admin.status_inactive')}</span>
                </label>
              </div>
            </div>

            {/* Rol */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                {t('common.status')}
              </label>
              <div className="grid grid-cols-3 gap-3">
                {[
                  { value: 'operador', label: 'Operador', icon: User },
                  { value: 'supervisor', label: 'Supervisor', icon: Shield },
                  { value: 'administrador', label: 'Administrador', icon: Crown }
                ].map((rol) => {
                  const Icon = rol.icon;
                  const isSelected = formData.rol_en_empresa === rol.value;
                  
                  return (
                    <button
                      key={rol.value}
                      type="button"
                      onClick={() => handleRolChange(rol.value as any)}
                      className={`p-3 border-2 rounded-lg transition-colors ${
                        isSelected
                          ? 'border-green-500 bg-green-50'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      <div className="flex flex-col items-center space-y-2">
                        <Icon className={`h-5 w-5 ${
                          isSelected ? 'text-green-600' : 'text-gray-400'
                        }`} />
                        <span className={`text-sm font-medium ${
                          isSelected ? 'text-green-700' : 'text-gray-600'
                        }`}>
                          {rol.label}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Configuraciones */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Configuraciones
              </label>
              <div className="space-y-3">
                <label className="flex items-center space-x-3 p-3 border border-gray-200 rounded-lg">
                  <input
                    type="checkbox"
                    checked={formData.es_empresa_principal}
                    onChange={(e) => setFormData(prev => ({ ...prev, es_empresa_principal: e.target.checked }))}
                    className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                  />
                  <div>
                    <span className="text-sm font-medium text-gray-900">Empresa principal</span>
                    <p className="text-xs text-gray-600">Esta es la empresa principal del usuario</p>
                  </div>
                </label>
              </div>
            </div>

            {/* Permisos */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Permisos específicos
              </label>
              <div className="space-y-3">
                {[
                  { key: 'puede_crear_reportes', label: 'Crear reportes', desc: 'Puede crear nuevos reportes' },
                  { key: 'puede_editar_reportes_propios', label: 'Editar reportes propios', desc: 'Puede editar sus propios reportes' },
                  { key: 'puede_ver_reportes_otros', label: 'Ver reportes de otros', desc: 'Puede ver reportes de otros usuarios' },
                  { key: 'puede_editar_reportes_otros', label: 'Editar reportes de otros', desc: 'Puede editar reportes de otros usuarios' },
                  { key: 'puede_aprobar_reportes', label: 'Aprobar reportes', desc: 'Puede aprobar reportes pendientes' },
                  { key: 'puede_gestionar_referencias', label: 'Gestionar referencias', desc: 'Puede gestionar faenas, predios, etc.' },
                  { key: 'puede_gestionar_usuarios', label: 'Gestionar usuarios', desc: 'Puede gestionar usuarios de la empresa' }
                ].map((permiso) => (
                  <label key={permiso.key} className="flex items-start space-x-3 p-3 border border-gray-200 rounded-lg">
                    <input
                      type="checkbox"
                      checked={formData.permisos?.[permiso.key as keyof typeof formData.permisos] || false}
                      onChange={(e) => handlePermisoChange(permiso.key, e.target.checked)}
                      className="mt-0.5 rounded border-gray-300 text-green-600 focus:ring-green-500"
                    />
                    <div>
                      <span className="text-sm font-medium text-gray-900">{permiso.label}</span>
                      <p className="text-xs text-gray-600">{permiso.desc}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                {hasChanges && (
                  <button
                    type="button"
                    onClick={handleReset}
                    className="text-sm text-gray-600 hover:text-gray-800 transition-colors"
                  >
                    {t('common.retry')}
                  </button>
                )}
              </div>

              <div className="flex items-center space-x-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-md transition-colors"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={!hasChanges || actualizarUsuario.isLoading}
                  className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-md transition-colors disabled:opacity-50 flex items-center space-x-2"
                >
                  <Save className="h-4 w-4" />
                  <span>
                    {actualizarUsuario.isLoading ? t('common.saving') : t('common.save')}
                  </span>
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditUsuarioEmpresaModal;
